import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatMoney, BACKEND_URL } from "@/lib/api";
import CountrySwitcher from "@/components/CountrySwitcher";
import { useCountry } from "@/lib/country";
import { Plus, Minus, Trash, FilePdf } from "@phosphor-icons/react";
import { toast } from "sonner";
import PricingDisclaimer, { PricingNote } from "@/components/PricingDisclaimer";

const CATEGORY_LABELS = {
  business_setup: "Business Setup",
  business_presence: "Business Presence",
  business_growth: "Business Growth",
  investment_expansion: "Investment & Expansion",
  professional_referrals: "Professional Referrals",
};

export default function Calculator() {
  const { countrySlug } = useCountry();
  const [services, setServices] = useState([]);
  const [country, setCountry] = useState(null);
  const [selections, setSelections] = useState({}); // {slug: {months, qty}}
  const [quote, setQuote] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get(`/services?country=${countrySlug}`).then((r) => setServices(r.data));
    api.get(`/countries/${countrySlug}`).then((r) => setCountry(r.data));
    setSelections({});
    setQuote(null);
  }, [countrySlug]);

  useEffect(() => {
    const list = Object.entries(selections).map(([service_slug, v]) => ({ service_slug, months: v.months, quantity: v.qty }));
    if (list.length === 0) { setQuote(null); return; }
    api.post("/calculator/quote", { country_slug: countrySlug, selections: list })
      .then((r) => setQuote(r.data));
  }, [selections, countrySlug]);

  const toggle = (slug) => {
    setSelections((s) => {
      const next = { ...s };
      if (next[slug]) delete next[slug];
      else next[slug] = { months: 12, qty: 1 };
      return next;
    });
  };

  const changeMonths = (slug, delta) => {
    setSelections((s) => ({ ...s, [slug]: { ...s[slug], months: Math.max(1, (s[slug].months || 1) + delta) } }));
  };
  const changeQty = (slug, delta) => {
    setSelections((s) => ({ ...s, [slug]: { ...s[slug], qty: Math.max(1, (s[slug].qty || 1) + delta) } }));
  };

  const grouped = services.reduce((acc, s) => { (acc[s.category] = acc[s.category] || []).push(s); return acc; }, {});
  const ccy = country?.currency_code || "CAD";

  const requestProposal = () => {
    if (!quote) { toast.error("Select at least one service first"); return; }
    sessionStorage.setItem("gos_quote", JSON.stringify({ country: countrySlug, selections, quote }));
    navigate("/contact?from=calculator");
  };

  const [showProposalModal, setShowProposalModal] = useState(false);
  const [proposalForm, setProposalForm] = useState({ client_name: "", client_email: "", client_company: "", client_phone: "", notes: "", validity_days: 30 });
  const [generating, setGenerating] = useState(false);

  const generateProposal = async () => {
    if (!quote || Object.keys(selections).length === 0) { toast.error("Add at least one service"); return; }
    if (!proposalForm.client_name || !proposalForm.client_email) { toast.error("Name and email required"); return; }
    setGenerating(true);
    try {
      const list = Object.entries(selections).map(([service_slug, v]) => ({ service_slug, months: v.months, quantity: v.qty }));
      const { data } = await api.post("/proposals", {
        country_slug: countrySlug, selections: list,
        ...proposalForm,
      });
      toast.success(`Proposal ${data.number} generated`);
      window.open(`${BACKEND_URL}/api/proposals/${data.id}/pdf`, "_blank");
      setShowProposalModal(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to generate proposal");
    } finally { setGenerating(false); }
  };

  return (
    <div data-testid="calculator-page" className="fade-in py-20">
      <div className="container-gos">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-8">
          <div>
            <div className="overline mb-3">Cost calculator</div>
            <h1 className="font-display text-4xl sm:text-5xl text-gos-charcoal tracking-tight">Plan your budget with precision.</h1>
            <p className="text-gos-slate mt-4 max-w-2xl leading-relaxed">Select services, choose duration, and review one-time + recurring costs in local currency. All pricing is editable from the admin panel.</p>
          </div>
          <div>
            <div className="overline mb-2">Country</div>
            <CountrySwitcher />
          </div>
        </div>

        <PricingNote className="mb-10 max-w-3xl" />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-10">
            {Object.entries(grouped).map(([cat, items]) => (
              <div key={cat}>
                <div className="overline text-gos-navy mb-4">{CATEGORY_LABELS[cat]}</div>
                <div className="space-y-2">
                  {items.map((s) => {
                    const sel = selections[s.slug];
                    return (
                      <div key={s.slug} data-testid={`calc-service-${s.slug}`} className={`border rounded-sm transition-colors ${sel ? "border-gos-navy bg-white" : "border-gos-border bg-white"}`}>
                        <div className="flex items-center justify-between p-4 gap-4">
                          <label className="flex items-center gap-3 cursor-pointer flex-1">
                            <input type="checkbox" data-testid={`calc-check-${s.slug}`} checked={!!sel} onChange={() => toggle(s.slug)}
                              className="w-4 h-4 accent-gos-navy" />
                            <div>
                              <div className="font-medium text-gos-charcoal text-sm">{s.name}</div>
                              <div className="text-xs text-gos-muted font-mono mt-1">
                                {s.price?.one_time ? `${formatMoney(s.price.one_time, ccy)} one-time` : ""}
                                {s.price?.one_time && s.price?.monthly ? " · " : ""}
                                {s.price?.monthly ? `${formatMoney(s.price.monthly, ccy)}/mo` : ""}
                              </div>
                            </div>
                          </label>
                          {sel && (
                            <div className="flex items-center gap-4 text-xs">
                              {s.price?.monthly > 0 && (
                                <div className="flex items-center gap-2">
                                  <span className="text-gos-muted">Months</span>
                                  <button onClick={() => changeMonths(s.slug, -1)} className="w-7 h-7 border border-gos-border hover:border-gos-navy"><Minus size={12} /></button>
                                  <span className="w-6 text-center font-mono">{sel.months}</span>
                                  <button onClick={() => changeMonths(s.slug, +1)} className="w-7 h-7 border border-gos-border hover:border-gos-navy"><Plus size={12} /></button>
                                </div>
                              )}
                              <div className="flex items-center gap-2">
                                <span className="text-gos-muted">Qty</span>
                                <button onClick={() => changeQty(s.slug, -1)} className="w-7 h-7 border border-gos-border hover:border-gos-navy"><Minus size={12} /></button>
                                <span className="w-6 text-center font-mono">{sel.qty}</span>
                                <button onClick={() => changeQty(s.slug, +1)} className="w-7 h-7 border border-gos-border hover:border-gos-navy"><Plus size={12} /></button>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          <aside className="lg:sticky lg:top-24 self-start bg-white border border-gos-border rounded-sm p-6">
            <div className="overline mb-3">Your estimate</div>
            <div className="text-sm text-gos-muted mb-4">{country?.name} · {ccy}</div>

            {!quote && <div className="text-sm text-gos-muted">Select services to see your live quote.</div>}

            {quote && (
              <>
                <div className="space-y-3 max-h-72 overflow-auto mb-6 font-mono text-xs">
                  {quote.lines.map((l) => (
                    <div key={l.service_slug} className="flex justify-between gap-2 border-b border-gos-border pb-2" data-testid={`quote-line-${l.service_slug}`}>
                      <div>
                        <div className="text-gos-charcoal">{l.service_name}</div>
                        <div className="text-gos-muted">{l.one_time ? formatMoney(l.one_time, ccy) + " one-time" : ""}{l.monthly ? ` · ${formatMoney(l.monthly, ccy)}/mo × ${l.months}` : ""}</div>
                      </div>
                      <div className="text-gos-charcoal">{formatMoney(l.subtotal, ccy)}</div>
                    </div>
                  ))}
                </div>

                <div className="space-y-2 font-mono text-xs border-t border-gos-border pt-4">
                  <Row label="One-time total" value={formatMoney(quote.one_time_total, ccy)} testid="quote-one-time" />
                  <Row label="Monthly total" value={formatMoney(quote.monthly_total, ccy)} testid="quote-monthly" />
                  <Row label="First month" value={formatMoney(quote.first_month, ccy)} testid="quote-first" />
                  <Row label="3-month" value={formatMoney(quote.three_month, ccy)} />
                  <Row label="6-month" value={formatMoney(quote.six_month, ccy)} />
                  <Row label="12-month" value={formatMoney(quote.twelve_month, ccy)} testid="quote-twelve" />
                  <Row label="Est. govt fees" value={formatMoney(quote.estimated_govt_fees, ccy)} />
                  <Row label="Est. 3rd-party fees" value={formatMoney(quote.estimated_third_party, ccy)} />
                  <div className="pt-3 mt-3 border-t border-gos-border flex justify-between font-display text-lg text-gos-navy">
                    <span>Grand total</span>
                    <span data-testid="quote-grand-total">{formatMoney(quote.grand_total, ccy)}</span>
                  </div>
                </div>

                <button onClick={requestProposal} className="btn-primary w-full mt-6" data-testid="request-proposal-btn">Request proposal</button>
                <button onClick={() => setShowProposalModal(true)} className="btn-sand w-full mt-2 text-xs" data-testid="generate-pdf-btn">
                  <FilePdf size={14} weight="bold" /> Generate PDF proposal
                </button>
                <button onClick={() => { setSelections({}); }} className="btn-secondary w-full mt-2 text-xs"><Trash size={12} /> Clear</button>
                <p className="text-[10px] text-gos-muted mt-4 leading-relaxed">Currency conversions are approximate and for reference purposes only. Government fees, legal, accounting and banking fees may apply separately.</p>
              </>
            )}
          </aside>
        </div>

        <div className="mt-12">
          <PricingDisclaimer />
        </div>

        {showProposalModal && (
          <div data-testid="proposal-modal" className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-6">            <div className="bg-white rounded-sm border border-gos-border max-w-md w-full p-6">
              <div className="overline mb-2">Generate proposal</div>
              <h3 className="font-display text-2xl text-gos-navy mb-4">Your details for the PDF</h3>
              <div className="space-y-3">
                <input data-testid="proposal-name" value={proposalForm.client_name} onChange={(e) => setProposalForm({ ...proposalForm, client_name: e.target.value })} placeholder="Full name *" className="w-full border border-gos-border rounded-sm px-3 py-2 text-sm" />
                <input data-testid="proposal-email" value={proposalForm.client_email} onChange={(e) => setProposalForm({ ...proposalForm, client_email: e.target.value })} placeholder="Email *" className="w-full border border-gos-border rounded-sm px-3 py-2 text-sm" />
                <input data-testid="proposal-company" value={proposalForm.client_company} onChange={(e) => setProposalForm({ ...proposalForm, client_company: e.target.value })} placeholder="Company" className="w-full border border-gos-border rounded-sm px-3 py-2 text-sm" />
                <input data-testid="proposal-phone" value={proposalForm.client_phone} onChange={(e) => setProposalForm({ ...proposalForm, client_phone: e.target.value })} placeholder="Phone" className="w-full border border-gos-border rounded-sm px-3 py-2 text-sm" />
                <div className="flex items-center gap-2 text-sm">
                  <label className="text-gos-muted">Validity (days)</label>
                  <input type="number" value={proposalForm.validity_days} onChange={(e) => setProposalForm({ ...proposalForm, validity_days: Number(e.target.value) })} className="w-20 border border-gos-border rounded-sm px-2 py-1 text-sm" />
                </div>
              </div>
              <div className="flex gap-2 mt-6 justify-end">
                <button onClick={() => setShowProposalModal(false)} className="btn-secondary text-xs !py-2">Cancel</button>
                <button onClick={generateProposal} disabled={generating} data-testid="proposal-generate-btn" className="btn-primary text-xs !py-2">
                  {generating ? "Generating…" : "Generate PDF"}
                </button>
              </div>
              <p className="text-[10px] text-gos-muted mt-4">A copy will be emailed to you and saved to your account. Valid for the number of days set above.</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Row({ label, value, testid }) {
  return (
    <div className="flex justify-between" data-testid={testid}>
      <span className="text-gos-muted">{label}</span>
      <span className="text-gos-charcoal">{value}</span>
    </div>
  );
}

function ProposalModalInline() { return null; }
